import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, IconButton, Chip, TextField, Drawer, List, ListItem, ListItemText, ListItemSecondaryAction,
  InputAdornment, Divider, Stack, Avatar, Badge, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Alert,
  Snackbar, CircularProgress, alpha, useTheme, Grid, Card, CardContent
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { styled } from '@mui/material/styles';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
  Star as StarIcon,
  FiberManualRecord as DotIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Save as SaveIcon,
  ContentCopy as CloneIcon,
  Print as PrintIcon,
  CheckCircle as CheckIcon,
  Restaurant as RestaurantIcon,
  AccessTime as AccessTimeIcon,
  LocalDining as LocalDiningIcon,
  EmojiFoodBeverage as EmojiFoodBeverageIcon
} from '@mui/icons-material';
import { format, addDays, eachDayOfInterval, parse, startOfWeek, endOfWeek, subDays, subWeeks } from 'date-fns';
import axiosInstance from '../../lib/axios';
import { toast, ToastContainer } from 'react-toastify';
import { selectGlobalProperty } from '../../redux/propertySlice';
import { useSelector } from 'react-redux';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../GlobalFunction/userPermissions';
import PrintDialog from './PrintDialog';

const MEAL_TYPES = [
  { id: 'Breakfast', label: 'Breakfast', time: '7:00 AM - 9:00 AM', icon: '🍳', color: '#f59e0b', mealTypeId: 2 },
  { id: 'Lunch', label: 'Lunch', time: '12:30 PM - 2:30 PM', icon: '🍛', color: '#10b981', mealTypeId: 3 },
  { id: 'Snacks', label: 'Snacks', time: '5:00 PM - 6:00 PM', icon: '🍪', color: '#8b5cf6', mealTypeId: 4 },
  { id: 'Dinner', label: 'Dinner', time: '7:30 PM - 9:30 PM', icon: '🍽️', color: '#ef4444', mealTypeId: 5 }
];

const DAYS_SHORT = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

const HeaderPaper = styled(Paper)(() => ({
  borderRadius: 0,
  borderBottom: 'none',
  padding: '16px 20px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
}));

const DayTab = styled(Box)(({ active }) => ({
  flex: 1,
  minWidth: 130,
  textAlign: 'center',
  padding: '3px 8px',
  cursor: 'pointer',
  borderBottom: active ? '3px solid #f59e0b' : '3px solid transparent',
  color: active ? '#f59e0b' : '#94a3b8',
  fontWeight: active ? 700 : 500,
  transition: 'all 0.3s',
  userSelect: 'none',
  background: active ? 'rgba(245, 158, 11, 0.1)' : 'transparent',
  borderRadius: '12px 12px 0 0',
  '&:hover': {
    background: 'rgba(245, 158, 11, 0.05)',
    color: '#f59e0b'
  },
}));

const HotelMealCard = styled(Card)(({ mealcolor }) => ({
  borderRadius: '20px',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  background: '#ffffff',
  border: `1px solid ${alpha(mealcolor, 0.2)}`,
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  height: '300px',        // ← fixed total card height (was height: '100%')
  minHeight: '300px',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: `0 16px 40px ${alpha(mealcolor, 0.2)}`,
    borderColor: mealcolor,
  },
}));

const ScrollableFoodList = styled(Box)(() => ({
  flex: 1,
  overflowY: 'auto',
  height: '220px',        // ← fixed height (was maxHeight: 200px)
  minHeight: '220px',     // ← add this
  maxHeight: '220px',
  padding: '4px',
  '&::-webkit-scrollbar': { width: '6px' },
  '&::-webkit-scrollbar-track': { background: '#f1f5f9', borderRadius: '10px' },
  '&::-webkit-scrollbar-thumb': {
    background: '#cbd5e1', borderRadius: '10px',
    '&:hover': { background: '#0d9488' },
  },
}));

const FoodItemCard = styled(Box)(({ mealcolor }) => ({
  background: '#faf9ff',
  borderRadius: '14px',
  padding: '4px 6px',
  marginBottom: '8px',
  border: `1px solid ${alpha(mealcolor, 0.15)}`,
  transition: 'all 0.2s',
  cursor: 'pointer',
  '&:hover': {
    background: alpha(mealcolor, 0.08),
    transform: 'translateX(1px)',
    borderColor: mealcolor,
  },
}));

const AddItemCard = styled(Box)(() => ({
  border: '2px dashed #cbd5e1',
  borderRadius: '16px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '212px',
  minHeight: '212px',
  maxHeight: '212px',
  padding: '20px 12px',
  cursor: 'pointer',
  background: 'linear-gradient(135deg, #f8fafc, #ffffff)',
  transition: 'all 0.3s',
  '&:hover': {
    borderColor: '#0d9488',
    background: 'linear-gradient(135deg, #f0fdfa, #ffffff)',
    transform: 'scale(0.98)',
  },
}));

const MealHeader = styled(Box)(({ mealcolor }) => ({
  background: `linear-gradient(135deg, ${mealcolor}15, ${mealcolor}05)`,
  padding: '4px 10px',
  borderBottom: `2px solid ${mealcolor}`,
  minHeight: '60px',
  display: 'flex',
  alignItems: 'center',
}));

export default function HostelMenuPlanner() {
  const [drawerFoods, setDrawerFoods] = useState([]);
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    const monday = startOfWeek(now, { weekStartsOn: 1 });
    return monday;
  });

  const [endDate, setEndDate] = useState(() => {
    const now = new Date();
    const monday = startOfWeek(now, { weekStartsOn: 1 });
    const sunday = addDays(monday, 6);
    return sunday;
  });

  const [days, setDays] = useState(() => {
    const now = new Date();
    const monday = startOfWeek(now, { weekStartsOn: 1 });
    const sunday = addDays(monday, 6);
    return eachDayOfInterval({ start: monday, end: sunday });
  });
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [mealData, setMealData] = useState([]);
  const [localMenu, setLocalMenu] = useState({});
  const [drawer, setDrawer] = useState({ open: false, dateKey: null, mealType: null });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFoodIds, setSelectedFoodIds] = useState([]);
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const [loading, setLoading] = useState(false);
  const [validationConflicts, setValidationConflicts] = useState([]);
  const [cloning, setCloning] = useState(false);
  const permissions = usePermissions("Day Wise Food");
  const globalProperty = useSelector(selectGlobalProperty);

  useEffect(() => {
    const diffInDays = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));

    if (diffInDays > 6) {
      setEndDate(addDays(startDate, 6));
    }

    if (endDate < startDate) {
      setEndDate(addDays(startDate, 6));
    }
  }, [startDate]);

  const handleExit = () => {
    navigate('/food-transport');
  }

  const handleEndDateChange = (newEndDate) => {
    if (!newEndDate) return;

    const diffInDays = Math.round((newEndDate - startDate) / (1000 * 60 * 60 * 24));
    if (diffInDays > 6) {
      setSnack({ open: true, msg: 'Date range cannot exceed 7 days (1 week)!', sev: 'error' });
      setEndDate(addDays(startDate, 6));
    } else if (newEndDate < startDate) {
      setSnack({ open: true, msg: 'End date cannot be before start date!', sev: 'error' });
      setEndDate(addDays(startDate, 6));
    } else {
      setEndDate(newEndDate);
    }
  };

  const getData = async () => {
    try {
      const response = await axiosInstance.post('MstFood/GetMstFoodDashBoard', {
        start: 1, PageSize: 100, SearchText: ''
      });
      if (response.data?.responseStatusCode === 1) {
        setDrawerFoods(response.data?.Data || []);
      }
    } catch (err) {
      toast.error('No Record Found');
    }
  };

  const getMealData = async (from, to) => {
    setLoading(true);
    try {
      const propId = globalProperty || 0;
      const payload = {
        FoodWeekCalDt: format(from, 'yyyy-MM-dd'),
        FoodWeekCalToDt: format(to, 'yyyy-MM-dd'),
        PropId: propId,
        TrnFoodWeekCalId: 1,
        MealTypeId: 1,
        YearId: 0,
        MonthId: 1,
        MonthNm: '',
        DayNm: '',
        Flag: ''
      };
      const response = await axiosInstance.post('MstFood/RetriveTrnFoodWeekCalAll', payload);
      if (response.data?.responseStatusCode === 1) {
        const list = response.data?.Data?.FoodWeekCalList || [];
        setMealData(list);
        buildLocalMenu(list);
      } else {
        toast.error(response.data?.Message || 'Failed to fetch meal calendar.');
      }
    } catch (err) {
      toast.error('Failed to fetch meal calendar.');
    } finally {
      setLoading(false);
    }
  };

  const buildLocalMenu = (list) => {
    const built = {};
    list.forEach(dayObj => {
      const dateKey = format(new Date(dayObj.FoodWeekCalDt), 'yyyy-MM-dd');
      built[dateKey] = { Breakfast: [], Lunch: [], Snacks: [], Dinner: [] };
      (dayObj.FoodWeekCalList || []).forEach(item => {
        const mealType = item.MealTypeName;
        if (built[dateKey][mealType]) {
          built[dateKey][mealType].push({
            FoodId: item.FoodId,
            FoodName: item.FoodName,
            FoodImg: item.FoodImg,
            FoodSubGrpImg: item.FoodSubGrpImg,
            MealTypeName: item.MealTypeName,
            TrnFoodWeekCalDtlId: item.TrnFoodWeekCalDtlId,
            TrnFoodWeekCalId: item.TrnFoodWeekCalId,
          });
        }
      });
    });
    setLocalMenu(built);
  };

  const resetMenuData = () => {
    setLocalMenu({});
    setMealData([]);
    setValidationConflicts([]);
  };

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (globalProperty) {
      resetMenuData();
      getMealData(startDate, endDate);
    } else {
      resetMenuData();
    }
  }, [globalProperty]);

  const handleLoadWeek = () => {
    if (!startDate || !endDate) return;

    const diffInDays = Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));

    if (diffInDays < 0) {
      setSnack({ open: true, msg: 'End date cannot be before start date!', sev: 'error' });
      return;
    }

    if (diffInDays >= 7) {
      setSnack({ open: true, msg: 'Date range cannot exceed 7 days (1 week)!', sev: 'error' });
      return;
    }

    const newDays = eachDayOfInterval({ start: startDate, end: endDate });
    setDays(newDays);
    setLocalMenu({});
    getMealData(startDate, endDate);
  };

  const handleClone = async () => {
    setCloning(true);
    try {
      const lastWeekStart = subWeeks(startDate, 1);
      const lastWeekEnd = subWeeks(endDate, 1);
      const propId = globalProperty || 0;
      const payload = {
        FoodWeekCalDt: format(lastWeekStart, 'yyyy-MM-dd'),
        FoodWeekCalToDt: format(lastWeekEnd, 'yyyy-MM-dd'),
        PropId: propId,
        TrnFoodWeekCalId: 1,
        MealTypeId: 1,
        YearId: 0,
        MonthId: 1,
        MonthNm: '',
        DayNm: '',
        Flag: ''
      };

      const response = await axiosInstance.post('MstFood/RetriveTrnFoodWeekCalAll', payload);

      if (response.data?.responseStatusCode === 1) {
        const lastWeekData = response.data?.Data?.FoodWeekCalList || [];

        if (lastWeekData.length === 0) {
          toast.warning('No menu data found for last week to clone!');
          setCloning(false);
          return;
        }

        const clonedMenu = {};

        lastWeekData.forEach((dayObj, index) => {
          if (index < days.length) {
            const currentDateKey = format(days[index], 'yyyy-MM-dd');
            clonedMenu[currentDateKey] = { Breakfast: [], Lunch: [], Snacks: [], Dinner: [] };
            (dayObj.FoodWeekCalList || []).forEach(item => {
              const mealType = item.MealTypeName;
              if (clonedMenu[currentDateKey][mealType]) {
                clonedMenu[currentDateKey][mealType].push({
                  FoodId: item.FoodId,
                  FoodName: item.FoodName,
                  FoodImg: item.FoodImg,
                  FoodSubGrpImg: item.FoodSubGrpImg,
                  MealTypeName: item.MealTypeName,
                  TrnFoodWeekCalDtlId: 0,
                  TrnFoodWeekCalId: 0,
                });
              }
            });
          }
        });

        setLocalMenu(prev => {
          const merged = { ...prev };
          Object.keys(clonedMenu).forEach(dateKey => {
            if (!merged[dateKey]) {
              merged[dateKey] = clonedMenu[dateKey];
            } else {
              Object.keys(clonedMenu[dateKey]).forEach(mealType => {
                if (!merged[dateKey][mealType] || merged[dateKey][mealType].length === 0) {
                  merged[dateKey][mealType] = clonedMenu[dateKey][mealType];
                }
              });
            }
          });
          return merged;
        });
        toast.success('Last week\'s menu cloned successfully!');
        setSnack({ open: true, msg: 'Last week\'s menu cloned successfully!', sev: 'success' });
      } else {
        toast.warning('No menu data found for last week to clone!');
      }
    } catch (err) {
      toast.error('Failed to clone last week\'s menu. Please try again.');
    } finally {
      setCloning(false);
    }
  };

  const openDrawer = (dateKey, mealType) => {
    setDrawer({ open: true, dateKey, mealType });
    setSearchTerm('');
    const current = (localMenu[dateKey]?.[mealType] || []).map(f => f.FoodId);
    setSelectedFoodIds(current);
  };

  const toggleFood = (foodId) => {
    setSelectedFoodIds(prev =>
      prev.includes(foodId) ? prev.filter(x => x !== foodId) : [...prev, foodId]
    );
  };

  const handleSaveDrawer = () => {
    const { dateKey, mealType } = drawer;
    const newItems = selectedFoodIds.map(id => {
      const found = drawerFoods.find(f => f.FoodId === id);
      return {
        FoodId: id,
        FoodName: found?.FoodName || '',
        MealTypeName: mealType,
        // FoodImg: found?.FoodImg || '',
        FoodSubGrpImg: found?.FoodSubGrpImg || '',
      };
    });
    setLocalMenu(prev => ({
      ...prev,
      [dateKey]: {
        ...(prev[dateKey] || { Breakfast: [], Lunch: [], Snacks: [], Dinner: [] }),
        [mealType]: newItems,
      }
    }));
    setDrawer({ open: false, dateKey: null, mealType: null });
    setSnack({ open: true, msg: 'Menu updated successfully!', sev: 'success' });
  };

  const removeItem = (dateKey, mealType, foodId) => {
    setLocalMenu(prev => ({
      ...prev,
      [dateKey]: {
        ...prev[dateKey],
        [mealType]: (prev[dateKey]?.[mealType] || []).filter(f => f.FoodId !== foodId),
      }
    }));
  };

  const validateMenuForPublish = () => {
    const conflicts = [];

    days.forEach((day, index) => {
      const dateKey = format(day, 'yyyy-MM-dd');
      const dayMenu = localMenu[dateKey] || {};
      const dayName = format(day, 'EEEE');

      const hasItems = Object.values(dayMenu).some(foods => foods.length > 0);
      if (!hasItems) {
        conflicts.push({
          type: 'warning',
          msg: `${dayName} (${format(day, 'dd/MM/yyyy')}) has no menu items added!`
        });
      }

      for (const mealType of MEAL_TYPES) {
        const foods = dayMenu[mealType.id] || [];
        const uniqueFoods = new Set(foods.map(f => f.FoodId));
        if (foods.length !== uniqueFoods.size) {
          conflicts.push({
            type: 'error',
            msg: `${dayName} - ${mealType.label} has duplicate food items!`
          });
        }
      }
    });

    return conflicts;
  };

  const handlePublish = async () => {
    // const conflicts = validateMenuForPublish();

    // if (conflicts.length > 0) {
    //   setValidationConflicts(conflicts);
    //   setPublishOpen(true);
    //   return;
    // }
    await publishMenu();
  };

  const publishMenu = async () => {
    setPublishing(true);

    try {
      const payload = [];
      const mealTypeMap = {
        'Breakfast': 2,
        'Lunch': 3,
        'Snacks': 4,
        'Dinner': 5
      };

      for (const day of days) {
        const dateKey = format(day, 'yyyy-MM-dd');
        const dayMenu = localMenu[dateKey] || {};
        const dayName = format(day, 'EEEE');
        const yearId = day.getFullYear();
        const monthId = day.getMonth() + 1;
        const monthName = format(day, 'MMMM');

        const foodEntities = [];

        for (const mealType of MEAL_TYPES) {
          const foods = dayMenu[mealType.id] || [];

          foods.forEach(food => {
            foodEntities.push({
              TrnFoodWeekCalDtlId: food.TrnFoodWeekCalDtlId || 0,
              TrnFoodWeekCalId: 0,
              MealTypeId: mealType.mealTypeId,
              FoodId: food.FoodId
            });
          });
        }

        if (foodEntities.length > 0) {
          payload.push({
            TrnFoodWeekCalId: 0,
            FoodWeekCalDt: dateKey,
            PropId: globalProperty || 0,
            YearId: yearId,
            MonthId: monthId,
            MonthNm: monthName,
            DayNm: dayName,
            Remark: `${dayName} menu`,
            Status: "1",
            UserId: 1,
            mstFoodEntities: foodEntities
          });
        }
      }

      if (payload.length === 0) {
        toast.warning('No menu items to publish! Please add some items first.');
        setPublishOpen(false);
        return;
      }

      const response = await axiosInstance.post('MstFood/MangeTrnFoodWeekCal', payload);

      if (response.data?.responseStatusCode === 1) {
        setSnack({ open: true, msg: 'Menu published successfully!', sev: 'success' });
        setPublishOpen(false);
        await getMealData(startDate, endDate);
      } else {
        toast.error(response.data?.responseMessage || 'Failed to publish menu.');
      }

    } catch (err) {
      toast.error('Failed to publish menu. Please try again.');
    } finally {
      setPublishing(false);
    }
  };

  const handleDeleteFood = async (id) => {
    try {
      const response = await axiosInstance.post('/MstFood/DeleteTrnFoodWeekCalDtl',
        {
          TrnFoodWeekCalDtlId: id,
          TrnFoodWeekCalId: 1,
          FoodId: 1,
        }
      );

      if (response.data.Status === 0) {
        toast.success(response.data?.Message || 'Food item deleted successfully!');
      } else {
        toast.error(response.data?.Message || 'Failed to delete food item.');
      }
    } catch (err) {
      toast.error('An error occurred while deleting the food item.');
    }
  };

  const handleDeleteAllFoodForDay = async (day) => {
    const dateKey = format(day, 'yyyy-MM-dd');
    const dayMenu = localMenu[dateKey] || {};
    const allFoods = [];
    Object.values(dayMenu).forEach(foods => {
      foods.forEach(food => {
        if (food.TrnFoodWeekCalDtlId) {
          allFoods.push(food);
        }
      });
    });

    if (allFoods.length === 0) {
      toast.warning('No saved items to delete for this day.');
      return;
    }

    try {
      const deletePromises = allFoods.map(food =>
        axiosInstance.post('/MstFood/DeleteTrnFoodWeekCalDtl', {
          TrnFoodWeekCalDtlId: food.TrnFoodWeekCalDtlId,
          TrnFoodWeekCalId: food.TrnFoodWeekCalId,
          FoodId: 1,
        })
      );

      await Promise.all(deletePromises);

      setLocalMenu(prev => ({
        ...prev,
        [dateKey]: { Breakfast: [], Lunch: [], Snacks: [], Dinner: [] }
      }));

      toast.success(`All food items deleted for ${format(day, 'EEEE')}!`);
    } catch (err) {
      toast.error('Failed to delete food items. Please try again.');
    }
  };

  const filteredDrawerFoods = useMemo(() => {
    if (!searchTerm) return drawerFoods;
    return drawerFoods.filter(f =>
      f.FoodName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [drawerFoods, searchTerm]);

  const handlePrintClick = () => {
    setPrintDialogOpen(true);
  };

  const getValidImageUrl = (url) => {
    if (!url || url === 'null' || url === 'undefined') return null;

    let cleanUrl = url.trim();

    cleanUrl = cleanUrl.replace(/(https?:\/\/)|(\/)+/g, "$1$2");

    if (cleanUrl.startsWith('http:/') && !cleanUrl.startsWith('http://')) {
      cleanUrl = cleanUrl.replace('http:/', 'http://');
    }
    if (cleanUrl.startsWith('https:/') && !cleanUrl.startsWith('https://')) {
      cleanUrl = cleanUrl.replace('https:/', 'https://');
    }

    if (cleanUrl && !cleanUrl.startsWith('http')) {

    }

    return cleanUrl;
  };

  const FoodImage = ({ src, alt, size = 40 }) => {
    const [imgError, setImgError] = useState(false);
    const validSrc = getValidImageUrl(src);

    if (!validSrc || imgError) {
      return (
        <Box sx={{
          width: size,
          height: size,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f0f0f0',
          borderRadius: 2
        }}>
          <Typography sx={{ fontSize: size * 0.6 }}>🍽️</Typography>
        </Box>
      );
    }

    return (
      <img
        src={validSrc}
        alt={alt || 'Food item'}
        style={{
          width: size,
          height: size,
          objectFit: 'cover',
          borderRadius: 8
        }}
        onError={() => setImgError(true)}
      />
    );
  };

  const handlePrint = () => {
    const startDateStr = format(startDate, 'dd/MM/yyyy');
    const endDateStr = format(endDate, 'dd/MM/yyyy');
    const printContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Weekly Menu - ${startDateStr} to ${endDateStr}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        @page {
          size: landscape;
          margin: 4mm;
        }
        
        body {
          font-family: 'Segoe UI', Arial, sans-serif;
          padding: 0;
          margin: 0;
          background: white;
          color: #333;
        }
        
        .print-container {
          width: 100%;
          padding: 3px;
        }
        
        .header {
          text-align: center;
          margin-bottom: 6px;
          padding-bottom: 4px;
          border-bottom: 2px solid #f59e0b;
        }
        
        .header h1 {
          color: #1a1a2e;
          font-size: 14px;
          margin-bottom: 2px;
        }
        
        .header p {
          color: #666;
          font-size: 8px;
        }
        
        .date-range {
          background: linear-gradient(135deg, #f59e0b, #fbbf24);
          color: white;
          padding: 2px 5px;
          border-radius: 4px;
          display: inline-block;
          margin-top: 2px;
          font-size: 8px;
        }
        

        .menu-container {
          display: flex;
          flex-direction: row;
          gap: 5px;
          margin-top: 5px;
          justify-content: space-between;
        }

        .day-card {
          flex: 1;
          min-width: 0;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          overflow: hidden;
          background: white;
          break-inside: avoid;
          page-break-inside: avoid;
        }
        
        .day-header {
          background: linear-gradient(135deg, #1a1a2e, #16213e);
          color: white;
          padding: 3px;
          text-align: center;
          border-bottom: 2px solid #f59e0b;
        }
        
        .day-header h3 {
          font-size: 10px;
          margin-bottom: 1px;
          font-weight: 600;
        }
        
        .day-header p {
          font-size: 7px;
          color: #94a3b8;
        }
        
        .meals-wrapper {
          padding: 4px;
        }
        
        .meal-section {
          margin-bottom: 4px;
          border: 1px solid #f0f0f0;
          border-radius: 4px;
          overflow: hidden;
        }
        
        .meal-title {
          background: #f8fafc;
          padding: 2px 4px;
          font-weight: bold;
          font-size: 8px;
          display: flex;
          align-items: center;
          gap: 3px;
          border-bottom: 1px solid #e2e8f0;
        }
        
        .meal-icon {
          font-size: 9px;
        }
        
        .meal-time {
          font-size: 6px;
          color: #666;
          margin-left: auto;
        }
        
        .food-list {
          padding: 2px 3px;
        }
        
        .food-item {
          font-size: 7.5px;
          padding: 2px 0;
          color: #444;
          border-bottom: 1px dashed #eee;
          display: flex;
          align-items: center;
          gap: 3px;
        }
        
        .food-item:last-child {
          border-bottom: none;
        }
        
        .food-image {
          width: 16px;
          height: 16px;
          object-fit: cover;
          border-radius: 2px;
          flex-shrink: 0;
        }
        
        .food-name {
          flex: 1;
          line-height: 1.2;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        .empty-message {
          color: #999;
          font-size: 7px;
          font-style: italic;
          padding: 3px 4px;
          text-align: center;
        }
        
        .footer {
          text-align: center;
          margin-top: 6px;
          padding-top: 4px;
          border-top: 1px solid #e2e8f0;
          font-size: 6px;
          color: #999;
        }
        
        @media print {
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          @page {
            size: landscape;
            margin: 4mm;
          }
          
          .menu-container {
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: nowrap !important;
            gap: 5px !important;
          }
          
          .day-card {
            flex: 1 !important;
            min-width: 0 !important;
            break-inside: avoid;
            page-break-inside: avoid;
          }
          
          .food-name {
            white-space: normal;
            word-break: break-word;
          }
          
          .food-image {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
        }
      </style>
    </head>
    <body>
      <div class="print-container">
        <div class="header">
          <h1>🍽️ Weekly Menu </h1>
          <p>${mealData[0]?.PropName || 'Clanbridge Living'}</p>
          <div class="date-range">
            📅 ${startDateStr} - ${endDateStr}
          </div>
        </div>
        
        <div class="menu-container">
        ${days.map(day => {
      const dateKey = format(day, 'yyyy-MM-dd');
      const dayMenu = localMenu[dateKey] || {};
      const dayName = format(day, 'EEEE');
      const displayDate = format(day, 'dd/MM/yyyy');

      return `
        <div class="day-card">
          <div class="day-header">
            <h3>${dayName.substring(0, 3)}</h3>
            <p>${displayDate}</p>
          </div>
          <div class="meals-wrapper">
            ${MEAL_TYPES.map(meal => {
        const foods = dayMenu[meal.id] || [];
        const isEmpty = foods.length === 0;

        const getValidImageUrl = (url) => {
          if (!url || url === 'null' || url === 'undefined') return null;
          let cleanUrl = url.trim();
          if (cleanUrl.startsWith('http:/') && !cleanUrl.startsWith('http://')) {
            cleanUrl = cleanUrl.replace('http:/', 'http://');
          }
          if (cleanUrl.startsWith('https:/') && !cleanUrl.startsWith('https://')) {
            cleanUrl = cleanUrl.replace('https:/', 'https://');
          }
          return cleanUrl;
        };

        return `
              <div class="meal-section">
                <div class="meal-title">
                  <span class="meal-icon">${meal.icon}</span>
                  <span>${meal.label.substring(0, 100)}</span>
                  <span class="meal-time">${meal.time}</span>
                </div>
                ${isEmpty ?
            '<div class="empty-message">—</div>' :
            `<div class="food-list">
              ${foods.map(food => {
              const imageUrl = getValidImageUrl(food.FoodSubGrpImg);
              const foodName = food.FoodName || 'Food Item';
              const shortName = foodName.length > 20 ? foodName.substring(0, 100) + '..' : foodName;
              return `
                  <div class="food-item">
                    ${imageUrl ?
                  `<img src="${imageUrl}" class="food-image" onerror="this.style.display='none'" />` :
                  ''
                }
                  <span class="food-name" title="${foodName}">${shortName}</span>
                  </div>
                `;
            }).join('')}
              </div>`
          }
              </div>
            `;
      }).join('')}
                </div>
              </div>
            `;
    }).join('')}
        </div>
        
        <div class="footer">
          <p>Generated on ${format(new Date(), 'dd/MM/yyyy hh:mm a')}</p>
        </div>
      </div>
      
      <script>
        window.onload = () => {
          setTimeout(() => {
            window.print();
            window.onafterprint = () => {
              window.close();
            };
          }, 100);
        };
      </script>
    </body>
    </html>
  `;

    const printWindow = window.open('', '_blank', 'width=1200,height=600');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ToastContainer
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #f0f2f5 100%)', fontFamily: 'Inter, sans-serif' }}>
        <HeaderPaper elevation={0}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} alignItems="flex-end">
                <Box flex={1}>
                  {/* <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#475569', mb: 0.5 }}>Start Date</Typography> */}
                  <DatePicker
                    value={startDate}
                    onChange={setStartDate}
                    format="dd/MM/yyyy"
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    label='Start Date'
                  />
                </Box>
                <Box flex={1}>
                  {/* <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#475569', mb: 0.5 }}>End Date</Typography> */}
                  <DatePicker
                    value={endDate}
                    onChange={handleEndDateChange}
                    format="dd/MM/yyyy"
                    minDate={startDate}
                    maxDate={addDays(startDate, 6)}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    label='End Date'
                  />
                </Box>
                <Button variant="contained" onClick={handleLoadWeek}
                  sx={{ height: 40, background: 'linear-gradient(135deg,#0d9488,#0891b2)', borderRadius: 3, textTransform: 'none', fontWeight: 700, flexShrink: 0, boxShadow: '0 2px 8px rgba(13,148,136,0.3)' }}>
                  Get Data
                </Button>
                <Tooltip
                  title={permissions?.AddRec == 0 ? "You don't have permission to publish menu" : "Publish Menu"}
                  arrow
                >
                  <span>
                    <Button
                      disabled={permissions?.AddRec == 0}
                      variant="contained"
                      // startIcon={<PrintIcon />}
                      onClick={handlePublish}
                      sx={{ height: 40, background: 'green', fontWeight: 700, borderRadius: 3, textTransform: 'none', '&:hover': { background: '#3b8132' } }}>
                      Publish
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Box>
            <Stack direction="row" gap={1.5}>
              <Button
                variant="outlined"
                startIcon={cloning ? <CircularProgress size={20} sx={{ color: '#f59e0b' }} /> : <CloneIcon />}
                onClick={handleClone}
                disabled={cloning}
                sx={{ borderColor: '#f59e0b', color: '#f59e0b', fontWeight: 600, borderRadius: 3, textTransform: 'none', '&:hover': { borderColor: '#fbbf24', background: 'rgba(245,158,11,0.1)' } }}>
                {cloning ? 'Cloning...' : 'Clone Last Week'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
                sx={{ borderColor: '#0d9488', color: '#0d9488', fontWeight: 600, borderRadius: 3, textTransform: 'none', '&:hover': { borderColor: '#0891b2', background: 'rgba(13,148,136,0.1)' } }}>
                Print
              </Button>
              <Tooltip title="Print the Details and Summary Ingredients" arrow>
                <Button
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  onClick={() => setPrintDialogOpen(true)}
                  sx={{ borderColor: '#2587f0', color: '#2587f0', fontWeight: 600, borderRadius: 3, textTransform: 'none', '&:hover': { borderColor: '#fbbf24', background: 'rgba(245,158,11,0.1)' } }}>
                  Requirement
                </Button>
              </Tooltip>

              <Tooltip title="Exit" arrow>
                <Button
                  variant="contained"
                  startIcon={<ExitToAppIcon />}
                  onClick={handleExit}
                  sx={{
                    background: 'linear-gradient(45deg, #6366f1, #4f46e5)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #4f46e5, #4338ca)',
                    },
                    borderRadius: 2,
                    minWidth: 80
                  }}
                >
                  Exit
                </Button>
              </Tooltip>
            </Stack>
          </Stack>
        </HeaderPaper>

        <Box sx={{ px: 3, mb: 1 }}>
          <Stack direction="row" sx={{ overflowX: 'auto', gap: 1, p: 1, }}>
            {days.map((d, i) => (
              <DayTab key={i} active={i === 0 ? 1 : 0}>
                <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>
                  {format(d, 'dd')}
                </Typography>
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                  <Typography sx={{ fontSize: 11, fontWeight: 500 }}>
                    {DAYS_SHORT[d.getDay()]}
                  </Typography>
                  {i === 0 && <ArrowDownIcon sx={{ fontSize: 12 }} />}
                </Stack>
              </DayTab>
            ))}
          </Stack>
        </Box>

        <Box sx={{ px: 2, pb: 4 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress sx={{ color: '#0d9488' }} />
            </Box>
          ) : (
            <Grid container spacing={1}>
              {days.map((d, di) => {
                const dateKey = format(d, 'yyyy-MM-dd');
                const displayDate = format(d, 'dd/MM/yyyy');
                const dayMenu = localMenu[dateKey] || {};

                return (
                  <Grid item xs={12} sm={6} md={3} lg={1.7} key={di}>
                    <Paper elevation={0} sx={{
                      borderRadius: '24px',
                      overflow: 'hidden',
                      background: '#ffffff',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                      transition: 'all 0.3s',
                      '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 16px 32px rgba(0,0,0,0.12)' }
                    }}>
                      <Box sx={{
                        background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
                        p: 0.5, textAlign: 'center', borderBottom: '3px solid #f59e0b'
                      }}>
                        <Typography sx={{ fontWeight: 800, color: '#ffffff', fontFamily: 'Playfair Display, serif', fontSize: 16 }}>
                          {DAYS_SHORT[d.getDay()]}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                          {displayDate}
                        </Typography>
                        <Tooltip title={`Delete all food for ${DAYS_SHORT[d.getDay()]}`} arrow>
                          <DeleteIcon
                            onClick={() => handleDeleteAllFoodForDay(d)}
                            sx={{
                              fontSize: 16,
                              cursor: 'pointer',
                              ml: 0.5,
                              color: '#f87c10',
                              verticalAlign: 'middle',
                              '&:hover': { color: '#ef4444' }
                            }}
                          />
                        </Tooltip>
                      </Box>

                      <Box sx={{ p: 0.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {MEAL_TYPES.map(meal => {
                          const foods = dayMenu[meal.id] || [];
                          const isEmpty = foods.length === 0;
                          const isSpecial = meal.id === 'Dinner' && (di === 2 || di === 4);

                          return (
                            <HotelMealCard key={meal.id} mealcolor={meal.color}>
                              <MealHeader mealcolor={meal.color}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Stack direction="row" spacing={0.4} alignItems="center">
                                    <Typography sx={{ fontSize: 18 }}>{meal.icon}</Typography>
                                    <Box>
                                      <Typography sx={{ fontWeight: 800, fontSize: 14, color: meal.color }}>
                                        {meal.label}
                                        {isSpecial && <StarIcon sx={{ fontSize: 12, ml: 0.5, color: '#fbbf24', verticalAlign: 'middle' }} />}
                                      </Typography>
                                      <Stack direction="row" alignItems="center" spacing={0.5}>
                                        <AccessTimeIcon sx={{ fontSize: 10, color: '#94a3b8' }} />
                                        <Typography sx={{ fontSize: 9, color: '#64748b' }}>{meal.time}</Typography>
                                      </Stack>
                                    </Box>
                                  </Stack>
                                  {isSpecial && (
                                    <Chip label="SPECIAL" size="small"
                                      sx={{ height: 20, fontSize: 9, background: meal.color, color: 'white', fontWeight: 700 }} />
                                  )}
                                </Stack>
                              </MealHeader>

                              <ScrollableFoodList>
                                {isEmpty ? (
                                  <AddItemCard onClick={() => openDrawer(dateKey, meal.id)}>
                                    <AddIcon sx={{ color: '#94a3b8', fontSize: 28, mb: 1 }} />
                                    <Typography sx={{ fontSize: 11, color: '#64748b', textAlign: 'center' }}>
                                      Add {meal.label} Items
                                    </Typography>
                                  </AddItemCard>
                                ) : (
                                  <>
                                    {foods.map(food => {
                                      const fixImageUrl = (url) => {
                                        if (!url) return null;
                                        let fixedUrl = url.replace('http:/', 'http://');
                                        fixedUrl = fixedUrl.replace('https:/', 'https://');
                                        return fixedUrl;
                                      };
                                      const imageUrl = fixImageUrl(food.FoodSubGrpImg);

                                      return (
                                        <FoodItemCard key={food.FoodId} mealcolor={meal.color}>
                                          <Stack direction="row" alignItems="center" justifyContent="space-between">
                                            <Stack direction="row" spacing={1} alignItems="center" flex={1}>
                                              <FoodImage src={food.FoodSubGrpImg} alt={food.FoodName} size={22} />
                                              <Box flex={1}>
                                                <Typography sx={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>
                                                  {food.FoodName}
                                                </Typography>
                                              </Box>
                                            </Stack>
                                            <IconButton size="small"
                                              onClick={(e) => { e.stopPropagation(); removeItem(dateKey, meal.id, food.FoodId); }}
                                              sx={{ color: '#cbd5e1', '&:hover': { color: '#ef4444' } }}>
                                              <DeleteIcon sx={{ fontSize: 16 }} onClick={() => handleDeleteFood(food.TrnFoodWeekCalDtlId)} />
                                            </IconButton>
                                          </Stack>
                                        </FoodItemCard>
                                      );
                                    })}
                                    <Box sx={{ mt: 'auto', position: 'sticky', bottom: 0, width: '100%', background: '#fff', textAlign: 'center', pt: 0.5, pb: 0, }}>
                                      <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                                        onClick={() => openDrawer(dateKey, meal.id)}
                                        sx={{ mt: 0.5, fontSize: 11, color: meal.color, textTransform: 'none', fontWeight: 600, '&:hover': { background: alpha(meal.color, 0.1) } }}>
                                        Add More Items
                                      </Button>
                                    </Box>
                                  </>
                                )}
                              </ScrollableFoodList>
                            </HotelMealCard>
                          );
                        })}
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>

        <Drawer anchor="right" open={drawer.open}
          onClose={() => setDrawer({ open: false, dateKey: null, mealType: null })}
          PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, mt: '64px', height: 'calc(100vh - 60px)', borderRadius: '24px 0 0 24px', background: 'linear-gradient(135deg, #ffffff, #faf9ff)' } }}>
          <Box sx={{ background: 'linear-gradient(135deg, #1a1a2e, #16213e)', px: 2, py: 1, color: 'white' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography sx={{ fontSize: 20, fontWeight: 700, fontFamily: 'Playfair Display, serif' }}>
                  Food Selection
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#94a3b8', mt: 0.5 }}>
                  Adding items for{' '}
                  <Box component="span" sx={{ fontWeight: 700, color: '#f59e0b' }}>
                    {drawer.mealType?.toUpperCase()} •{' '}
                    {drawer.dateKey && format(new Date(drawer.dateKey), 'dd/MM/yyyy')}
                  </Box>
                </Typography>
              </Box>
              <IconButton onClick={() => setDrawer({ open: false })}>
                <CloseIcon color='error' />
              </IconButton>
            </Stack>
          </Box>

          <Box sx={{ px: 3, pt: 2, pb: 1 }}>
            <TextField fullWidth size="small" placeholder="Search dishes..."
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>,
                sx: { borderRadius: 4, background: '#f8fafc' }
              }} />
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1, maxHeight: 'calc(100vh - 220px)' }}>
            {filteredDrawerFoods.length === 0 ? (
              <Typography sx={{ textAlign: 'center', color: '#94a3b8', mt: 4, fontSize: 13 }}>No items found</Typography>
            ) : (
              filteredDrawerFoods.map(food => {
                const isSelected = selectedFoodIds.includes(food.FoodId);
                const fixImageUrl = (url) => {
                  if (!url) return null
                  let fixedUrl = url.replace('http:/', 'http://');
                  fixedUrl = fixedUrl.replace('https:/', 'https://');
                  return fixedUrl;
                };

                const imageUrl = fixImageUrl(food.FoodSubGrpImg);
                return (
                  <Paper key={food.FoodId} onClick={() => toggleFood(food.FoodId)}
                    sx={{
                      mb: 1.5, p: 1, borderRadius: 3,
                      border: isSelected ? '2px solid #0d9488' : '1px solid #e2e8f0',
                      background: isSelected ? '#f0fdfa' : '#fff',
                      cursor: 'pointer', transition: 'all 0.2s',
                      '&:hover': { transform: 'translateX(4px)', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }
                    }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <FoodImage src={food.FoodSubGrpImg} alt={food.FoodName} size={50} />
                        <Box>
                          <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#1e293b' }}>{food?.FoodName}</Typography>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap">
                            {food.Price && (
                              <Chip label={`₹${food.Price}`} size="small"
                                sx={{ fontSize: 9, height: 18, background: '#dbeafe', color: '#2563eb' }} />
                            )}
                            {food.FoodGrpName && (
                              <Chip label={food.FoodGrpName} size="small"
                                sx={{ fontSize: 9, height: 18, background: '#dbeafe', color: '#2563eb' }} />
                            )}
                            {food.FoodSubGrpName && (
                              <Chip label={food.FoodSubGrpName} size="small"
                                sx={{ fontSize: 9, height: 18, background: '#fef3c7', color: '#d97706' }} />
                            )}
                          </Stack>
                        </Box>
                      </Stack>
                      {isSelected
                        ? <CheckIcon sx={{ color: '#0d9488', fontSize: 24 }} />
                        : <AddIcon sx={{ color: '#94a3b8', fontSize: 24 }} />
                      }
                    </Stack>
                  </Paper>
                );
              })
            )}
          </Box>

          <Box sx={{ p: 2.5, borderTop: '1px solid #e2e8f0', background: '#fff' }}>
            <Stack direction="row" gap={1.5}>
              <Button fullWidth variant="outlined"
                onClick={() => setDrawer({ open: false })}
                sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 600, borderColor: '#e2e8f0', color: '#475569' }}>
                Cancel
              </Button>
              <Button fullWidth variant="contained" onClick={handleSaveDrawer}
                sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700, background: '#0f172a', '&:hover': { background: '#1e293b' } }}>
                Add to Menu
              </Button>
            </Stack>
          </Box>
        </Drawer>

        <Dialog open={publishOpen} maxWidth="sm" fullWidth onClose={() => setPublishOpen(false)}
          PaperProps={{ sx: { borderRadius: 4 } }}>
          <DialogTitle sx={{ background: 'linear-gradient(135deg, #1a1a2e, #16213e)', color: 'white' }}>
            <Stack direction="row" alignItems="center" gap={1.5}>
              <WarningIcon sx={{ color: '#f59e0b', fontSize: 28 }} />
              <Typography sx={{ fontSize: 22, fontWeight: 800, fontFamily: 'Playfair Display, serif' }}>
                Menu Validation
              </Typography>
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            {publishing ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress sx={{ color: '#0d9488' }} />
              </Box>
            ) : validationConflicts.length > 0 ? (
              <Box sx={{ background: '#fef2f2', borderRadius: 3, p: 2, mb: 2 }}>
                {validationConflicts.map((c, i) => (
                  <Alert key={i} severity={c.type} sx={{ mb: 1 }}>{c.msg}</Alert>
                ))}
              </Box>
            ) : (
              <Alert severity="success" icon={<CheckIcon />} sx={{ borderRadius: 2 }}>
                All validations passed! Your menu is ready to be published.
              </Alert>
            )}
          </DialogContent>
          {!publishing && (
            <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
              <Button variant="outlined" onClick={() => setPublishOpen(false)}>Cancel</Button>
              {validationConflicts.length === 0 && (
                <Button variant="contained" onClick={publishMenu}
                  sx={{ background: 'linear-gradient(135deg, #0d9488, #0891b2)' }}>
                  Publish Now
                </Button>
              )}
            </DialogActions>
          )}
        </Dialog>

        <Snackbar open={snack.open} autoHideDuration={2500}
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
          <Alert severity={snack.sev} variant="filled" sx={{ borderRadius: 3 }}>{snack.msg}</Alert>
        </Snackbar>

        <PrintDialog
          open={printDialogOpen}
          onClose={() => setPrintDialogOpen(false)}
          // selectedMeal={selectedMeal}
          globalProperty={globalProperty}
        />
      </Box>
    </LocalizationProvider>
  );
}